import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Upload, FileText, ArrowLeft, AlertTriangle, Check, X,
  TrendingDown, TrendingUp
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { parseCsv, guessColumnMapping, rowsToDrafts } from '../utils/csvParsing';
import { formatCurrency } from '../utils/invoiceData';
import { depositCategories } from '../utils/expenseData';
import { buildCategoryLookup } from './ExpenseCategories';
import './BankStatementImport.css';

const MAPPING_FIELDS = [
  { key: 'date', label: 'Date', required: true },
  { key: 'description', label: 'Description', required: true },
  { key: 'amount', label: 'Amount (signed)', required: false, hint: 'Use if one column has the signed amount' },
  { key: 'debit', label: 'Debit / Withdrawal', required: false, hint: 'Money out' },
  { key: 'credit', label: 'Credit / Deposit', required: false, hint: 'Money in' },
  { key: 'type', label: 'Type (Debit/Credit)', required: false, hint: 'Optional override column' }
];

function BankStatementImport() {
  const navigate = useNavigate();
  const { state, addExpense, addDeposit } = useApp();

  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [defaultAccountId, setDefaultAccountId] = useState('');
  const [defaultExpenseCategoryId, setDefaultExpenseCategoryId] = useState('');
  const [defaultDepositCategory, setDefaultDepositCategory] = useState('cleaning_service');
  const [selected, setSelected] = useState({}); // rowIndex -> boolean
  const [drafts, setDrafts] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const activeAccounts = useMemo(
    () => (state.accounts || []).filter(a => !a.archived).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [state.accounts]
  );
  const categories = useMemo(() => state.expenseCategories || [], [state.expenseCategories]);
  const categoryLookup = useMemo(() => buildCategoryLookup(categories), [categories]);
  const categoryOptions = useMemo(() => {
    const parents = categories.filter(c => !c.parentId).sort((a, b) => a.name.localeCompare(b.name));
    const childrenByParent = {};
    categories.forEach(c => {
      if (c.parentId) (childrenByParent[c.parentId] = childrenByParent[c.parentId] || []).push(c);
    });
    Object.keys(childrenByParent).forEach(k =>
      childrenByParent[k].sort((a, b) => a.name.localeCompare(b.name))
    );
    const result = [];
    parents.forEach(p => {
      result.push({ id: p.id, label: p.name });
      (childrenByParent[p.id] || []).forEach(child => {
        result.push({ id: child.id, label: `— ${child.name}` });
      });
    });
    categories
      .filter(c => c.parentId && !categoryLookup[c.parentId])
      .forEach(orphan => result.push({ id: orphan.id, label: orphan.name }));
    return result;
  }, [categories, categoryLookup]);

  const resetAll = () => {
    setFileName('');
    setParseError('');
    setHeaders([]);
    setRows([]);
    setMapping({});
    setSelected({});
    setDrafts([]);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFile = (file) => {
    if (!file) return;
    setParseError('');
    setImportResult(null);
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = reader.result;
        const { headers, rows } = parseCsv(text);
        if (headers.length === 0 || rows.length === 0) {
          setParseError('No rows found in this CSV.');
          setHeaders([]); setRows([]); setDrafts([]); setSelected({});
          return;
        }
        const guess = guessColumnMapping(headers);
        setHeaders(headers);
        setRows(rows);
        setMapping(guess);
        const newDrafts = rowsToDrafts(rows, guess);
        setDrafts(newDrafts);
        const sel = {};
        newDrafts.forEach(d => { if (d.valid) sel[d.rowIndex] = true; });
        setSelected(sel);
      } catch (err) {
        setParseError(err?.message || 'Failed to parse CSV.');
      }
    };
    reader.onerror = () => setParseError('Could not read file.');
    reader.readAsText(file);
  };

  const handleMappingChange = (field, value) => {
    const next = { ...mapping, [field]: value };
    setMapping(next);
    const newDrafts = rowsToDrafts(rows, next);
    setDrafts(newDrafts);
    const sel = {};
    newDrafts.forEach(d => { if (d.valid) sel[d.rowIndex] = true; });
    setSelected(sel);
  };

  const toggleRow = (idx) => {
    setSelected(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const toggleAll = (value) => {
    const sel = {};
    drafts.forEach(d => { if (d.valid) sel[d.rowIndex] = value; });
    setSelected(sel);
  };

  const validDrafts = drafts.filter(d => d.valid);
  const selectedDrafts = validDrafts.filter(d => selected[d.rowIndex]);
  const selectedExpenseTotal = selectedDrafts
    .filter(d => d.kind === 'expense')
    .reduce((s, d) => s + d.amount, 0);
  const selectedDepositTotal = selectedDrafts
    .filter(d => d.kind === 'deposit')
    .reduce((s, d) => s + d.amount, 0);
  const invalidCount = drafts.length - validDrafts.length;

  const mappingIsValid = () => {
    if (!mapping.date || !mapping.description) return false;
    if (!mapping.amount && !(mapping.debit || mapping.credit)) return false;
    return true;
  };

  const handleImport = async () => {
    if (selectedDrafts.length === 0) return;
    setImporting(true);
    let expensesCreated = 0;
    let depositsCreated = 0;
    const failures = [];
    for (const d of selectedDrafts) {
      try {
        if (d.kind === 'expense') {
          await addExpense({
            description: d.description,
            amount: d.amount,
            categoryId: defaultExpenseCategoryId || '',
            accountId: defaultAccountId || '',
            dueDate: d.date,
            paid: true,
            paidDate: d.date,
            notes: `Imported from ${fileName}`,
            reviewed: false,
            source: 'bank-import'
          });
          expensesCreated++;
        } else {
          await addDeposit({
            description: d.description,
            amount: d.amount,
            category: defaultDepositCategory || 'other_income',
            accountId: defaultAccountId || '',
            date: d.date,
            notes: `Imported from ${fileName}`,
            reviewed: false,
            source: 'bank-import'
          });
          depositsCreated++;
        }
      } catch (err) {
        failures.push({ draft: d, message: err?.message || 'Save failed' });
      }
    }
    setImporting(false);
    setImportResult({ expensesCreated, depositsCreated, failures });
  };

  const renderMappingRow = (field) => (
    <div className="map-row" key={field.key}>
      <label>
        {field.label}
        {field.required && <span className="req"> *</span>}
      </label>
      <select
        value={mapping[field.key] || ''}
        onChange={e => handleMappingChange(field.key, e.target.value)}
      >
        <option value="">— None —</option>
        {headers.map(h => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      {field.hint && <div className="hint">{field.hint}</div>}
    </div>
  );

  return (
    <div className="bank-import-page">
      <div className="bank-import-header">
        <div className="header-title">
          <Link to="/admin/expenses" className="back-link">
            <ArrowLeft size={16} /> Back to Expenses
          </Link>
          <h1>Import Bank Statement</h1>
          <div className="subtitle">
            Upload a CSV from your bank, map the columns, then review and import as deposits and expenses.
          </div>
        </div>
      </div>

      {/* ====== STEP 1: Upload ====== */}
      <section className="bank-import-section">
        <div className="section-header">
          <div className="step-badge">1</div>
          <h2>Upload CSV</h2>
        </div>
        <div className="upload-box">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={e => handleFile(e.target.files?.[0])}
          />
          {fileName ? (
            <div className="file-picked">
              <FileText size={18} /> <span>{fileName}</span>
              <button className="link-btn" onClick={resetAll}>Clear</button>
            </div>
          ) : (
            <div className="upload-prompt">
              <Upload size={20} /> Choose a CSV file
            </div>
          )}
        </div>
        {parseError && (
          <div className="import-error"><AlertTriangle size={16} /> {parseError}</div>
        )}
      </section>

      {/* ====== STEP 2: Column mapping & defaults ====== */}
      {headers.length > 0 && (
        <section className="bank-import-section">
          <div className="section-header">
            <div className="step-badge">2</div>
            <h2>Map Columns & Defaults</h2>
          </div>
          <p className="section-hint">
            We pre-filled guesses based on your CSV headers. Adjust if anything looks wrong.
            Use either a single signed "Amount" column OR separate Debit/Credit columns.
          </p>
          <div className="map-grid">
            {MAPPING_FIELDS.map(renderMappingRow)}
          </div>

          <div className="defaults-grid">
            <div className="map-row">
              <label>Default Account</label>
              <select value={defaultAccountId} onChange={e => setDefaultAccountId(e.target.value)}>
                <option value="">— None —</option>
                {activeAccounts.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              <div className="hint">Applied to every imported row. You can change each later.</div>
            </div>
            <div className="map-row">
              <label>Default Expense Category</label>
              <select value={defaultExpenseCategoryId} onChange={e => setDefaultExpenseCategoryId(e.target.value)}>
                <option value="">— Uncategorized —</option>
                {categoryOptions.map(o => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
              <div className="hint">Used for imported expenses.</div>
            </div>
            <div className="map-row">
              <label>Default Deposit Category</label>
              <select value={defaultDepositCategory} onChange={e => setDefaultDepositCategory(e.target.value)}>
                {depositCategories.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <div className="hint">Used for imported deposits.</div>
            </div>
          </div>

          {!mappingIsValid() && (
            <div className="import-warning">
              <AlertTriangle size={16} />
              Please map at least <strong>Date</strong>, <strong>Description</strong>, and either <strong>Amount</strong> or <strong>Debit/Credit</strong>.
            </div>
          )}
        </section>
      )}

      {/* ====== STEP 3: Review & import ====== */}
      {headers.length > 0 && mappingIsValid() && (
        <section className="bank-import-section">
          <div className="section-header">
            <div className="step-badge">3</div>
            <h2>Review Transactions</h2>
          </div>

          <div className="preview-summary">
            <div className="summary-pill">
              <TrendingDown size={16} />
              <span>{selectedDrafts.filter(d => d.kind === 'expense').length} expenses</span>
              <strong>{formatCurrency(selectedExpenseTotal)}</strong>
            </div>
            <div className="summary-pill">
              <TrendingUp size={16} />
              <span>{selectedDrafts.filter(d => d.kind === 'deposit').length} deposits</span>
              <strong>{formatCurrency(selectedDepositTotal)}</strong>
            </div>
            {invalidCount > 0 && (
              <div className="summary-pill warn">
                <AlertTriangle size={16} />
                <span>{invalidCount} rows skipped</span>
              </div>
            )}
            <div className="preview-actions">
              <button className="link-btn" onClick={() => toggleAll(true)}>Select all</button>
              <button className="link-btn" onClick={() => toggleAll(false)}>Select none</button>
            </div>
          </div>

          <div className="preview-table-wrap">
            <table className="preview-table">
              <thead>
                <tr>
                  <th className="cb" />
                  <th>Date</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th className="right">Amount</th>
                  <th>Issue</th>
                </tr>
              </thead>
              <tbody>
                {drafts.map(d => (
                  <tr key={d.rowIndex} className={d.valid ? '' : 'invalid-row'}>
                    <td className="cb">
                      {d.valid && (
                        <input
                          type="checkbox"
                          checked={!!selected[d.rowIndex]}
                          onChange={() => toggleRow(d.rowIndex)}
                        />
                      )}
                    </td>
                    <td>{d.valid ? d.date : '—'}</td>
                    <td>{d.valid ? d.description : <span className="muted">(skipped)</span>}</td>
                    <td>
                      {d.valid ? (
                        d.kind === 'expense'
                          ? <span className="kind-badge expense">Expense</span>
                          : <span className="kind-badge deposit">Deposit</span>
                      ) : '—'}
                    </td>
                    <td className={`right ${d.valid ? (d.kind === 'expense' ? 'amt expense' : 'amt deposit') : ''}`}>
                      {d.valid ? formatCurrency(d.amount) : '—'}
                    </td>
                    <td className="muted">{d.valid ? '' : d.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="import-actions">
            <button className="btn-cancel" onClick={() => navigate('/admin/expenses')} disabled={importing}>
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={handleImport}
              disabled={importing || selectedDrafts.length === 0}
            >
              {importing ? 'Importing…' : `Import ${selectedDrafts.length} transaction${selectedDrafts.length === 1 ? '' : 's'}`}
            </button>
          </div>

          {importResult && (
            <div className="import-result">
              <Check size={18} />
              <div>
                <div><strong>Imported {importResult.expensesCreated + importResult.depositsCreated} transactions.</strong></div>
                <div className="hint">
                  {importResult.expensesCreated} expenses · {importResult.depositsCreated} deposits.
                  {importResult.failures.length > 0 && (
                    <> {importResult.failures.length} failed.</>
                  )}
                </div>
                <div className="result-links">
                  <Link to="/admin/expenses" className="link-btn">Review on Expenses page</Link>
                  <button className="link-btn" onClick={resetAll}>Import another file</button>
                </div>
              </div>
            </div>
          )}

          {importResult?.failures?.length > 0 && (
            <div className="import-error">
              <AlertTriangle size={16} />
              <div>
                Some rows could not be saved:
                <ul>
                  {importResult.failures.slice(0, 5).map((f, i) => (
                    <li key={i}>{f.draft.description} ({f.draft.date}) — {f.message}</li>
                  ))}
                  {importResult.failures.length > 5 && <li>…and {importResult.failures.length - 5} more</li>}
                </ul>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default BankStatementImport;
